<script setup lang="ts">
import { computed } from 'vue';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { resolveInsightAxisDisplay } from '@/components/home/insights/insight-axis-display';
import InsightAxisSkeleton from '@/components/home/insights/insight-axis-skeleton.vue';
import InsightHintPanel from '@/components/home/insights/insight-hint-panel.vue';
import InsightTopicHintPair from '@/components/home/insights/insight-topic-hint-pair.vue';
import InsightsGenerationError from '@/components/home/insights/insights-generation-error.vue';
import { useInsightAxisRetry } from '@/components/home/insights/use-insight-axis-retry';
import { provideInsightSources } from '@/components/home/insights/use-insight-sources';
import { provideInsightAxisTracking } from '@/components/home/insights/use-insight-tracking';
import {
  insightAxisPresentations,
  resolveInsightAxisErrorTitle,
} from '@/components/insights/insight-display';
import InsightHintTree from '@/components/insights/insight-hint-tree.vue';
import type { InsightAxis } from '@/types/insights';
import { featureFlags } from '@/featureFlags';

interface Props {
  axis: InsightAxis;
  hasSetupPersonalNewsOrFavoriteThemes: boolean;
  hasPersonalNewsArticles: boolean;
  hasFeaturedArticles: boolean;
}

const props = defineProps<Props>();

// 公開されるまでは出典もヒントも無い。公開で id が決まったら親が key を変えて作り直す
provideInsightSources(() =>
  props.axis.generation_status === 'processed' ? props.axis.sources : [],
);
provideInsightAxisTracking({
  id: props.axis.generation_status === 'processed' ? props.axis.id : 0,
  name: props.axis.axis,
});

const display = computed(() => resolveInsightAxisDisplay(props.axis, props));
const retryAxis = useInsightAxisRetry();
const isTopicHintPair = featureFlags.ANDEV_10719_INSIGHT_TOPIC_HINT_PAIR;
const errorTitle = isTopicHintPair
  ? resolveInsightAxisErrorTitle(props.axis.axis)
  : '発想のヒントの生成に失敗しました';
</script>

<template>
  <div class="insight-axis-panel" :class="{ paired: isTopicHintPair }">
    <div v-if="!isTopicHintPair" class="title c-title-medium--xm">
      <DgrIcon :name="insightAxisPresentations[axis.axis].icon" />
      {{ insightAxisPresentations[axis.axis].label }}
    </div>
    <div v-if="display.kind === 'notice'" class="no-data">
      <div class="no-data-text">
        <div class="c-title-bold--m">
          {{ display.notice.title }}
        </div>
        <div class="c-text-regular--m">
          {{ display.notice.message }}
        </div>
      </div>
      <RouterLink
        v-if="display.notice.showOnboardingLink"
        :to="{ name: 'onboardingWelcome' }"
        type="button"
        class="no-data-action c-btn c-btn--auto c-btn--small c-btn--AnewsPrimary"
      >
        各機能をセットアップする
      </RouterLink>
    </div>
    <InsightAxisSkeleton
      v-else-if="display.kind === 'loading'"
      class="loading"
    />
    <!-- 軸単位の再試行 API が無いため、発想のヒント全体を再生成する -->
    <InsightsGenerationError
      v-else-if="display.kind === 'error'"
      class="error"
      :title="errorTitle"
      message="申し訳ありませんが、再度お試しください"
      @retry="retryAxis"
    />
    <template v-else-if="isTopicHintPair">
      <InsightTopicHintPair
        v-for="hint in display.axis.hints"
        :key="hint.uuid"
        :hint="hint"
        :axis="display.axis.axis"
      />
    </template>
    <InsightHintTree
      v-else
      :topics="display.axis.topics"
      :hints="display.axis.hints"
    >
      <template #hint="{ hint }">
        <InsightHintPanel :hint="hint" />
      </template>
    </InsightHintTree>
  </div>
</template>

<style lang="scss" scoped>
.insight-axis-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &.paired {
    gap: 16px;

    // 外側の白い枠が無く、グレーの背景に直接置かれるため白背景にする。
    .no-data,
    .error {
      background-color: $color-white;
    }
  }

  .title {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  .no-data {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    padding: 20px;
    border: 1px solid $color-gray400;
    border-radius: 4px;

    .no-data-text {
      display: flex;
      flex-direction: column;
    }

    .no-data-action {
      flex-shrink: 0;
    }
  }
}
</style>
